import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, SettingsProfileResult, SettingsProfileUpdateInput } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

/** Query key prefix shared with the generic `user:{userId}` push-invalidation
 * skeleton (`ws/queryInvalidation.ts`) — a `settings.updateProfile` write
 * doesn't itself push (no live cross-device profile sync spec'd), but
 * keying under `['user', userId, ...]` keeps this consistent with every
 * other user-scoped read in this task and costs nothing. */
export function settingsProfileQueryKey(userId: string | null): readonly unknown[] {
  return ['user', userId, 'settings', 'profile'];
}

export interface UseSettingsProfileResult {
  profile: SettingsProfileResult | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  save: (input: SettingsProfileUpdateInput) => Promise<SettingsProfileResult>;
  isSaving: boolean;
}

/** F10 Profile section: `settings.getProfile` read + `settings.updateProfile` write. */
export function useSettingsProfile(): UseSettingsProfileResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && userId !== null;
  const queryKey = settingsProfileQueryKey(userId);

  const query = useQuery<SettingsProfileResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'settings.getProfile', undefined),
    enabled,
  });

  const mutation = useMutation<SettingsProfileResult, ErrorAlert, SettingsProfileUpdateInput>({
    mutationFn: (input) => requestTyped(wsClient, 'settings.updateProfile', input),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, result);
    },
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: useCallback(() => void query.refetch(), [query]),
    save: (input) => mutation.mutateAsync(input),
    isSaving: mutation.isPending,
  };
}
